import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

const GLYPHS = '/!<>-_\\[]{}=+*^?#';

/**
 * The page's motion, in one place. It borrows the film's grammar rather than a
 * library's: things arrive by rising a little out of a blur (power3-out), a
 * sentence is typed on its own progress rather than on a timer, and nothing
 * moves faster than someone can follow. Every hook degrades to "everything
 * shown, nothing moving" under prefers-reduced-motion.
 */

export const prefersReducedMotion = () =>
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

export function useReducedMotion() {
  const [reduced, setReduced] = useState(prefersReducedMotion);
  useEffect(() => {
    const media = matchMedia('(prefers-reduced-motion: reduce)');
    const change = () => setReduced(media.matches);
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);
  return reduced;
}

/** True while the element is on screen (and stays false when the tab is hidden). */
export function useInView<T extends Element>(ref: RefObject<T | null>, threshold = 0.2) {
  const [inView, setInView] = useState(false);
  const [visible, setVisible] = useState(() => !document.hidden);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setInView(entry!.isIntersecting), { threshold });
    observer.observe(el);
    const vis = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', vis);
    return () => { observer.disconnect(); document.removeEventListener('visibilitychange', vis); };
  }, [ref, threshold]);
  return inView && visible;
}

/**
 * How far through a tall, pinned section the reader is: 0 when its top reaches
 * the top of the viewport, 1 when its bottom reaches the bottom. The section
 * pins itself with `position: sticky` — this only reads where the scroll is, so
 * the page scrolls natively and nothing is hijacked.
 */
export function useScrollProgress<T extends HTMLElement>(ref: RefObject<T | null>) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let frame = 0;
    const read = () => {
      frame = 0;
      const rect = el.getBoundingClientRect();
      const span = rect.height - innerHeight;
      const p = span <= 0 ? 1 : Math.min(1, Math.max(0, -rect.top / span));
      setProgress(prev => (Math.abs(prev - p) < 0.0005 ? prev : p));
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(read); };
    read();
    addEventListener('scroll', onScroll, { passive: true });
    addEventListener('resize', onScroll);
    return () => { removeEventListener('scroll', onScroll); removeEventListener('resize', onScroll); if (frame) cancelAnimationFrame(frame); };
  }, [ref]);
  return progress;
}

/** The part of `p` that falls inside [from, to], as 0..1. */
export const span = (p: number, from: number, to: number) => Math.min(1, Math.max(0, (p - from) / (to - from)));

/** Text typed as a pure function of progress — scrubbing back un-types it. */
export const typed = (text: string, t: number) => text.slice(0, Math.round(text.length * Math.min(1, Math.max(0, t))));

/**
 * Scroll reveals for the sections that were written before this existed. They
 * are named by selector so none of those components had to change, and the
 * hiding class is added in a layout effect — before the first paint — so there
 * is no flash of content that then disappears. Whatever is already on screen
 * is left alone: the first screen has its own entrance.
 */
const REVEAL = [
  '.experience-heading', '.buyer-demo', '.product-benefits > div', '.use-cases .section-heading', '.use-case-grid article',
  '.demo-section-intro', '.showcase', '.hero-footnote', '.control-copy', '.control-example',
  '.kinetic-head', '.demo-choice', '.questions-section > div', '.questions-list details', '.pilot-grid > div', '.pilot-form',
].join(',');

export function useRevealOnScroll() {
  useLayoutEffect(() => {
    if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') return;
    splitHeadings();
    const els = [...document.querySelectorAll<HTMLElement>(REVEAL)];
    const pending: HTMLElement[] = [];
    for (const el of els) {
      const rect = el.getBoundingClientRect();
      const onFirstScreen = rect.top < innerHeight * 0.92 && rect.bottom > 0;
      if (onFirstScreen) continue;
      const siblings = el.parentElement ? [...el.parentElement.children].filter(c => c.matches(REVEAL)) : [];
      el.style.setProperty('--rv-i', String(Math.max(0, siblings.indexOf(el))));
      el.setAttribute('data-rv', '');
      pending.push(el);
    }
    document.documentElement.classList.add('rv-on');
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) if (entry.isIntersecting) { entry.target.setAttribute('data-rv', 'in'); observer.unobserve(entry.target); }
    }, { rootMargin: '0px 0px -9% 0px', threshold: 0.08 });
    pending.forEach(el => observer.observe(el));
    return () => { observer.disconnect(); document.documentElement.classList.remove('rv-on'); pending.forEach(el => el.removeAttribute('data-rv')); };
  }, []);
}

/** Types `text` once `active` turns true, one character at a time, never faster than a person reads along. */
export function useTypedOnce(text: string, active: boolean, startDelay = 0, perChar = 34) {
  const [n, setN] = useState(0);
  const pos = useRef(0);                       // survives StrictMode's mount/unmount/mount, so the second mount carries on instead of restarting
  const reduced = useReducedMotion();
  useEffect(() => {
    if (reduced) { setN(text.length); return; }
    if (!active || pos.current >= text.length) return;
    let timer = 0;
    const tick = () => { pos.current += 1; setN(pos.current); if (pos.current < text.length) timer = window.setTimeout(tick, perChar); };
    timer = window.setTimeout(tick, pos.current === 0 ? startDelay : perChar);
    return () => clearTimeout(timer);
  }, [active, text, startDelay, perChar, reduced]);
  return text.slice(0, n);
}

/* ---------------------------------------------------------------------------------------------
 * Scramble: text that decodes into its words. A column of glyphs is replaced left to right, each
 * character flickering through noise before it lands. It is the one idea taken whole from a
 * portfolio the team liked (astapable.com) — used where a mono label or a headline word *changes*,
 * never on body copy. It is deterministic (a hash, not Math.random) so a given frame is the same
 * every time, and it always ends on the exact target string.
 * ------------------------------------------------------------------------------------------- */
const hash = (i: number, k: number) => { let h = (i * 374761393 + k * 668265263) | 0; h = (h ^ (h >>> 13)) * 1274126177; return ((h ^ (h >>> 16)) >>> 0) / 4294967296; };

/** The text of the scramble at `t` (0..1) between two strings. */
export function scrambleFrame(from: string, to: string, t: number, frame: number): string {
  if (t >= 1) return to;
  const n = Math.max(from.length, to.length);
  let out = '';
  for (let i = 0; i < n; i++) {
    const land = (i / Math.max(1, n - 1)) * 0.62 + hash(i, 7) * 0.2;           // when this character lands
    const start = land - 0.28;                                                    // …and when it starts to flicker
    if (t >= land) out += to[i] ?? '';
    else if (t >= start) { const c = to[i] ?? from[i] ?? ' '; out += c === ' ' ? ' ' : GLYPHS[Math.floor(hash(i, frame) * GLYPHS.length)]; }
    else out += from[i] ?? '';
  }
  return out;
}

/** Runs a scramble, calling `set` each frame; returns a cancel function that leaves the target text. */
export function runScramble(set: (text: string) => void, from: string, to: string, ms = 900): () => void {
  if (prefersReducedMotion() || from === to) { set(to); return () => {}; }
  let raf = 0; const t0 = performance.now();
  const step = (now: number) => {
    const t = Math.min(1, (now - t0) / ms);
    set(scrambleFrame(from, to, t, Math.floor((now - t0) / 38)));
    if (t < 1) raf = requestAnimationFrame(step);
  };
  raf = requestAnimationFrame(step);
  return () => { cancelAnimationFrame(raf); set(to); };
}

/** A string that scrambles into each new `target`. First paint is the target itself (no flash of noise). */
export function useScrambled(target: string, ms = 900): string {
  const [text, setText] = useState(target);
  const shown = useRef(target);
  useEffect(() => {
    if (shown.current === target) return;
    const from = shown.current;
    shown.current = target;
    return runScramble(t => setText(t), from, target, ms);
  }, [target, ms]);
  return text;
}

const firstText = (el: Element): Text | null => {
  const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  for (let n = w.nextNode(); n; n = w.nextNode()) if (n.nodeValue && n.nodeValue.trim()) return n as Text;
  return null;
};

/** Scramble the first text node of an element in place, keeping its width. Restores the original text at the end. */
export function scrambleElement(el: HTMLElement, ms = 700) {
  if (prefersReducedMotion() || el.dataset.scrambling) return;
  const node = firstText(el);
  if (!node) return;
  const original = node.nodeValue ?? '';
  el.dataset.scrambling = '1';
  const w = el.offsetWidth;
  const keep = el.style.minWidth;
  el.style.minWidth = w + 'px';
  runScramble(t => { node.nodeValue = t; }, '', original, ms);
  setTimeout(() => { node.nodeValue = original; el.style.minWidth = keep; delete el.dataset.scrambling; }, ms + 60);
}

/**
 * Mono eyebrows decode the first time they scroll into view (the one on the first screen decodes on
 * load). Nothing else: buttons and links used to re-decode on hover, but a label that turns to noise
 * under the pointer is a worse button, so that went.
 */
export function useScrambleEverywhere() {
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const seen = new WeakSet<Element>();
    const io = new IntersectionObserver(entries => {
      for (const e of entries) if (e.isIntersecting && !seen.has(e.target)) { seen.add(e.target); io.unobserve(e.target); scrambleElement(e.target as HTMLElement, 900); }
    }, { threshold: 0.9 });
    // eyebrows that sit inside a pinned scene decode with their section, the rest on their own
    const watch = () => document.querySelectorAll<HTMLElement>('.eyebrow').forEach(el => { if (!seen.has(el)) io.observe(el); });
    watch();
    return () => io.disconnect();
  }, []);
}

/* Headings arrive a word at a time. The split is done once, in the DOM, before first paint, and only on
 * headings whose text never changes. */
const SPLIT = '.experience-heading h2, .use-cases .section-heading h2, .control-copy h2, .questions-section h2, .pilot-grid h2, .demo-section-intro h2';
export function splitHeadings(root: ParentNode = document) {
  root.querySelectorAll<HTMLElement>(SPLIT).forEach(h => {
    if (h.dataset.split) return;
    h.dataset.split = '';
    let i = 0;
    const walk = (node: Node) => {
      [...node.childNodes].forEach(c => {
        if (c.nodeType === Node.TEXT_NODE) {
          const parts = (c.nodeValue ?? '').split(/(\s+)/);
          const frag = document.createDocumentFragment();
          parts.forEach(part => {
            if (!part) return;
            if (/^\s+$/.test(part)) frag.append(document.createTextNode(' '));
            else { const w = document.createElement('span'); w.className = 'w'; w.style.setProperty('--wi', String(i++)); w.textContent = part; frag.append(w); }
          });
          c.parentNode!.replaceChild(frag, c);
        } else if (c.nodeType === Node.ELEMENT_NODE && (c as Element).tagName !== 'BR') walk(c);
      });
    };
    walk(h);
  });
}
